import { styled } from '@linaria/react';
import { MantineProvider } from '@mantine/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { theme } from '../../styles/theme';
import { ArtifactExportProvider } from '../flora-agent/artifacts/artifact-export-context';
import { getArtifactEntry } from '../flora-agent/artifacts/artifact-registry';
import type { ArtifactType } from '../flora/flora.type';
import '../flora-agent/artifacts/register-artifacts';
import { decodeArtifactPayload } from './artifact-export-route.helpers';
import type { ArtifactExportPayload } from './artifact-export-route.type';

const SUPPORTED_ARTIFACT_TYPES = new Set<ArtifactType>([
  'flow_by_phase_chart',
  'freeform_chart',
  'measure_comparison_chart',
  'measures_over_time_chart',
  'process_map_chart',
  'work_pace_chart',
]);

const READY_ATTRIBUTE = 'data-artifact-ready';

function getCurrentPayloadState(): { hash: string; payload: ArtifactExportPayload | null } {
  if (typeof window === 'undefined') return { hash: '', payload: null };
  return {
    hash: window.location.hash,
    payload: decodeArtifactPayload(window.location.hash),
  };
}

function ArtifactExportRoute(): ReactElement {
  const params = useParams<{ type: string }>();
  const type = params.type ?? '';

  const [payloadState, setPayloadState] = useState(getCurrentPayloadState);

  useEffect(() => {
    const syncPayloadState = () => {
      (window as unknown as { __artifactReady?: boolean }).__artifactReady = false;
      document.getElementById('artifact-root')?.removeAttribute(READY_ATTRIBUTE);
      setPayloadState(getCurrentPayloadState());
    };

    window.addEventListener('hashchange', syncPayloadState);
    return () => {
      window.removeEventListener('hashchange', syncPayloadState);
    };
  }, []);

  // Each export route instance gets its own QueryClient. We never fetch data here
  // — artifacts get their data via props — but some artifact-adjacent hooks may
  // touch the cache, so we instantiate one explicitly per render.
  const queryClient = useMemo(() => new QueryClient(), []);

  return (
    <MantineProvider theme={theme} defaultColorScheme="light">
      <QueryClientProvider client={queryClient}>
        <ArtifactExportProvider value={true}>
          <ArtifactExportFrame type={type} payload={payloadState.payload} payloadKey={payloadState.hash} />
        </ArtifactExportProvider>
      </QueryClientProvider>
    </MantineProvider>
  );
}

function ArtifactExportFrame({
  type,
  payload,
  payloadKey,
}: {
  type: string;
  payload: ArtifactExportPayload | null;
  payloadKey: string;
}): ReactElement {
  const entry = isSupportedArtifactType(type) ? getArtifactEntry(type) : undefined;
  const ArtifactComponent = entry?.component;

  const canRender = Boolean(ArtifactComponent && payload);
  const renderKey = `${type}:${payloadKey}`;
  const [readyForKey, setReadyForKey] = useState<string | null>(null);
  const ready = readyForKey === renderKey;

  useEffect(() => {
    (window as unknown as { __artifactReady?: boolean }).__artifactReady = false;
    if (!canRender) return;

    let raf1 = 0;
    let raf2 = 0;
    // Two requestAnimationFrame ticks: the first lets React commit and Recharts
    // begin its ResponsiveContainer measurement; the second lets the SVG paint
    // before we tell Playwright we're done.
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        setReadyForKey(renderKey);
        (window as unknown as { __artifactReady?: boolean }).__artifactReady = true;
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [canRender, renderKey]);

  if (!ArtifactComponent || !payload) {
    return (
      <ArtifactRoot id="artifact-root" data-artifact-error="true">
        Missing artifact
      </ArtifactRoot>
    );
  }

  const readyAttr = ready ? { [READY_ATTRIBUTE]: 'true' as const } : {};

  // Measure-comparison renders a single-axis chart with a tight header and
  // no work-period nav, so it looks better in a narrower, shorter frame
  // than the wide work-pace / flow-by-phase / process-map screenshots.
  const isCompact = type === 'measure_comparison_chart';

  // Multi-render stack mode: when the BE supplies `renders`, mount one
  // artifact instance per entry, stacked vertically in the same wrapper.
  // The whole stack ends up in one screenshot/PNG. Falls back to the
  // legacy single-render mode (driven by top-level `options`) when
  // `renders` is absent or empty for backward compatibility.
  if (payload.renders && payload.renders.length > 0) {
    return (
      <ArtifactRoot id="artifact-root" $compact={isCompact} {...readyAttr}>
        {payload.renders.map((entry, index) => (
          <ArtifactSection key={`${type}-${index}`}>
            {entry.pageTitle ? <ArtifactTitle>{entry.pageTitle}</ArtifactTitle> : null}
            <ArtifactComponent data={payload.data} config={payload.config} initialState={entry.options} />
          </ArtifactSection>
        ))}
      </ArtifactRoot>
    );
  }

  // BE writes `pageTitle` into the options bag (single-render mode) so the
  // route can render it as a chart heading. The artifact components ignore
  // the key. Validate it is actually a string before rendering — a non-string
  // value would crash React with "Objects are not valid as a React child".
  const pageTitleCandidate =
    typeof payload.options === 'object' && payload.options !== null
      ? (payload.options as Record<string, unknown>).pageTitle
      : undefined;
  const pageTitle = typeof pageTitleCandidate === 'string' ? pageTitleCandidate : undefined;

  return (
    <ArtifactRoot id="artifact-root" $compact={isCompact} {...readyAttr}>
      {pageTitle ? <ArtifactTitle>{pageTitle}</ArtifactTitle> : null}
      <ArtifactComponent data={payload.data} config={payload.config} initialState={payload.options} />
    </ArtifactRoot>
  );
}

/**
 * Type guard that narrows an arbitrary string to the `ArtifactType` union.
 *
 * @param {string} value - Candidate type discriminator from `useParams`.
 * @returns {boolean} True when the value is a known artifact type.
 */
function isSupportedArtifactType(value: string): value is ArtifactType {
  return SUPPORTED_ARTIFACT_TYPES.has(value as ArtifactType);
}

/**
 * Export viewport wrapper. Width matches the BE Playwright renderer's
 * viewport width (`ARTIFACT_RENDER_VIEWPORT_WIDTH` in `artifact_rendering.py`).
 * Height is content-driven so the locator screenshot does not trap white
 * space below short charts — the renderer screenshots the `#artifact-root`
 * locator's bounding box, not the full viewport.
 *
 * `$compact` is set for measure-comparison artifacts, which have a single-axis
 * chart and no work-period nav and therefore look better in a narrower,
 * tighter frame than the default.
 */
const ArtifactRoot = styled.div<{ $compact?: boolean }>`
  align-self: flex-start;
  flex: 0 0 auto;
  width: ${(props) => (props.$compact ? '800px' : '1000px')};
  padding: ${(props) => (props.$compact ? '12px 16px' : '20px 24px')};
  box-sizing: border-box;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? '6px' : '12px')};
`;

/**
 * Chart heading rendered above the artifact when the BE supplies a
 * `pageTitle` in the URL payload's `options` bag. Sized to read clearly
 * in Slack's inline image preview.
 */
const ArtifactTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--ink-dark, #1a1a1a);
  font-family: 'Figtree', sans-serif;
`;

/**
 * Visual section wrapping one chart in the multi-render stack. Adds a
 * small bottom border between adjacent charts so the user can tell the
 * stacked instances apart in the combined PNG. Last section has no
 * border so the image edge is clean.
 */
const ArtifactSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--sky-light, #e5e7eb);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

export { ArtifactExportRoute };
